import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface QuoteFormData {
  customerId: string;
  agentId: string;
  travelStartDate?: string;
  travelEndDate?: string;
  validUntil: string;
  adults: number;
  children: number;
  infants: number;
  notes?: string;
  internalNotes?: string;
}

interface QuoteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (quoteId: string) => void;
}

function getDefaultValidUntil(): string {
  const date = new Date();
  date.setDate(date.getDate() + 14); // Default: 14 days from now
  return date.toISOString().split('T')[0] ?? '';
}

export function QuoteModal({
  open,
  onOpenChange,
  onSuccess,
}: QuoteModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<QuoteFormData>({
    defaultValues: {
      adults: 2,
      children: 0,
      infants: 0,
      validUntil: getDefaultValidUntil(),
    },
  });

  // Fetch customers
  const { data: customersData } = trpc.customer.list.useQuery({
    page: 1,
  });

  // Fetch agents
  const { data: agents } = trpc.auth.listAgents.useQuery();

  const utils = trpc.useUtils();

  const createMutation = trpc.quote.create.useMutation({
    onSuccess: (quote) => {
      utils.quote.list.invalidate();
      utils.quote.stats.invalidate();
      reset();
      onOpenChange(false);
      onSuccess?.(quote.id);
      showSuccessToast('Quote created successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to create quote');
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      reset({
        adults: 2,
        children: 0,
        infants: 0,
        validUntil: getDefaultValidUntil(),
      });
    }
  }, [open, reset]);

  const onSubmit = (data: QuoteFormData) => {
    setIsSubmitting(true);
    createMutation.mutate({
      customerId: data.customerId,
      agentId: data.agentId,
      travelStartDate: data.travelStartDate ? new Date(data.travelStartDate) : undefined,
      travelEndDate: data.travelEndDate ? new Date(data.travelEndDate) : undefined,
      validUntil: new Date(data.validUntil),
      adults: data.adults,
      children: data.children,
      infants: data.infants,
      notes: data.notes,
      internalNotes: data.internalNotes,
    });
  };

  const customers = customersData?.customers ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Quote</DialogTitle>
          <DialogDescription>
            Create a new quote for a customer. You can add line items after creating the quote.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Customer Selection */}
          <div className="space-y-2">
            <Label>Customer *</Label>
            <Combobox
              options={customers.map((customer) => ({
                value: customer.id,
                label: `${customer.firstName} ${customer.lastName} (${customer.email})`,
              }))}
              value={watch('customerId') || ''}
              onValueChange={(value) => setValue('customerId', value)}
              placeholder="Select a customer"
              searchPlaceholder="Search customers..."
            />
            {errors.customerId && (
              <p className="text-sm text-red-500">{errors.customerId.message}</p>
            )}
          </div>

          {/* Agent Selection */}
          <div className="space-y-2">
            <Label>Agent *</Label>
            <Combobox
              options={agents?.map((agent) => ({
                value: agent.id,
                label: `${agent.firstName} ${agent.lastName}`,
              })) ?? []}
              value={watch('agentId') || ''}
              onValueChange={(value) => setValue('agentId', value)}
              placeholder="Select an agent"
              searchPlaceholder="Search agents..."
            />
            {errors.agentId && (
              <p className="text-sm text-red-500">{errors.agentId.message}</p>
            )}
          </div>

          {/* Travel Dates */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="travelStartDate">Travel Start Date</Label>
              <Input
                id="travelStartDate"
                type="date"
                {...register('travelStartDate')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="travelEndDate">Travel End Date</Label>
              <Input
                id="travelEndDate"
                type="date"
                {...register('travelEndDate')}
              />
            </div>
          </div>

          {/* Valid Until */}
          <div className="space-y-2">
            <Label htmlFor="validUntil">Valid Until *</Label>
            <Input
              id="validUntil"
              type="date"
              {...register('validUntil', { required: 'Validity date is required' })}
            />
            {errors.validUntil && (
              <p className="text-sm text-red-500">{errors.validUntil.message}</p>
            )}
          </div>

          {/* Travelers */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="adults">Adults *</Label>
              <Input
                id="adults"
                type="number"
                min="1"
                {...register('adults', {
                  required: 'Required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Min 1' },
                })}
              />
              {errors.adults && (
                <p className="text-sm text-red-500">{errors.adults.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="children">Children</Label>
              <Input
                id="children"
                type="number"
                min="0"
                {...register('children', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Min 0' },
                })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="infants">Infants</Label>
              <Input
                id="infants"
                type="number"
                min="0"
                {...register('infants', {
                  valueAsNumber: true,
                  min: { value: 0, message: 'Min 0' },
                })}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (visible to customer)</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder="Notes that will be visible on the quote..."
              rows={2}
            />
          </div>

          {/* Internal Notes */}
          <div className="space-y-2">
            <Label htmlFor="internalNotes">Internal Notes</Label>
            <Textarea
              id="internalNotes"
              {...register('internalNotes')}
              placeholder="Internal notes (not visible to customer)..."
              rows={2}
            />
          </div>

          {/* Error Messages */}
          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !watch('customerId') || !watch('agentId')}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Quote
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
